import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Gamepad2, Loader2, AlertTriangle, Tag, Sparkles } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

const hexA = (hex, a) => {
  const h = (hex || "#10b981").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const THEMES = {
  midnight: { bg: "#07070b", accent: "#10b981" },
  ember: { bg: "#120806", accent: "#f97316" },
  ocean: { bg: "#050b12", accent: "#06b6d4" },
  forest: { bg: "#07110a", accent: "#65a30d" },
  candy: { bg: "#120613", accent: "#db2777" },
  royal: { bg: "#0a0716", accent: "#7c3aed" },
};

export default function StoreView() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [event, setEvent] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const hitFiredRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(`/gtm/store/${slug}`)
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: null }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, error: "not found" }); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (state.data?.theme === "seasonal") {
      api.get("/events/active").then((r) => setEvent(r.data?.active ? r.data : null)).catch(() => {});
    }
  }, [state.data]);

  // Fire a single analytics ping (QR scans carry ?s=qr) + tick the seasonal countdown.
  useEffect(() => {
    if (!state.data || hitFiredRef.current) return;
    hitFiredRef.current = true;
    const isQr = new URLSearchParams(window.location.search).get("s") === "qr";
    api.post(`/gtm/store/${slug}/hit`, { kind: isQr ? "qr" : "view" }).catch(() => {});
  }, [state.data, slug]);
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(t); }, []);
  const sendHit = (kind) => { api.post(`/gtm/store/${slug}/hit`, { kind }).catch(() => {}); };

  if (state.loading) return <div className="min-h-screen grid place-items-center bg-[#07070b]"><Loader2 className="w-8 h-8 animate-spin text-white/70" /></div>;

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#07070b] text-center px-6">
        <div>
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <p className="text-white/80">This store page isn't available.</p>
          <Link to="/"><Button className="mt-4 rounded-full">Go to GameView Studio</Button></Link>
        </div>
      </div>
    );
  }

  const k = state.data;
  const base = THEMES[k.theme] || THEMES.midnight;
  const accent = (k.theme === "seasonal" && event?.accent) ? event.accent : base.accent;
  const bg = (k.theme === "seasonal") ? "#0a0a10" : base.bg;
  const grad = `radial-gradient(1100px 560px at 50% -10%, ${hexA(accent, 0.2)}, transparent)`;
  const shots = (k.screenshots || []).filter((s) => s.image_url);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const qrUrl = typeof window !== "undefined" ? `${window.location.origin}/store/${slug}?s=qr` : "";
  const shareText = `Play ${k.game_title} — made with GameView Studio!`;
  const fmtRemaining = (ms) => {
    if (ms <= 0) return null;
    const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
    if (d >= 1) return `${d} day${d > 1 ? "s" : ""}`;
    if (h >= 1) return `${h} hour${h > 1 ? "s" : ""}`;
    return `${m} minute${m !== 1 ? "s" : ""}`;
  };
  const countdown = (k.theme === "seasonal" && event?.ends_at) ? fmtRemaining(new Date(event.ends_at).getTime() - nowTick) : null;
  const openShare = (kind) => {
    sendHit("share");
    const u = encodeURIComponent(shareUrl); const t = encodeURIComponent(shareText);
    if (kind === "x") window.open(`https://twitter.com/intent/tweet?text=${t}&url=${u}`, "_blank", "noopener");
    else if (kind === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank", "noopener");
    else if (kind === "discord") navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => toast.success("Copied — paste it into Discord!"), () => toast.error("Couldn't copy the link."));
  };

  return (
    <div className="min-h-screen text-white" style={{ background: `${grad}, ${bg}` }} data-testid="store-view">
      <div className="max-w-4xl mx-auto px-4 py-10">
        {countdown && (
          <div className="rounded-xl px-4 py-2 mb-6 text-center text-sm font-semibold" style={{ background: hexA(accent, 0.15), border: `1px solid ${hexA(accent, 0.4)}` }} data-testid="store-countdown">
            {event.emoji} {event.name} ends in {countdown} — play before the season turns!
          </div>
        )}
        <div className="flex items-center justify-center gap-2 mb-6 text-white/40 text-[11px] font-semibold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> AI-generated store page · GameView Studio
        </div>

        {/* Header */}
        <div className="flex items-start gap-5 flex-wrap mb-8">
          {k.icon_url && <img src={mediaUrl(k.icon_url)} alt="App icon" className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover shadow-2xl shrink-0" data-testid="store-icon" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="store-title">{k.game_title}</h1>
              {k.category && <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-bold"><Tag className="w-3 h-3" /> {k.category}</span>}
            </div>
            {k.tagline && <p className="text-base sm:text-lg font-semibold text-white/90" data-testid="store-tagline">{k.tagline}</p>}
            {k.short_desc && <p className="text-sm text-white/60 mt-1">{k.short_desc}</p>}
            {k.game_id && (
              <Link to={`/g/${k.game_id}`} onClick={() => sendHit("play")}>
                <Button className="mt-4 rounded-full text-white border-0 hover:opacity-90" style={{ backgroundColor: accent }} data-testid="store-play-btn"><Gamepad2 className="w-4 h-4" /> Play this game</Button>
              </Link>
            )}
          </div>
        </div>

        {/* Screenshots */}
        {shots.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-3 mb-8 -mx-1 px-1" data-testid="store-screenshots">
            {shots.map((s, i) => (
              <div key={i} className="shrink-0 w-52 rounded-2xl overflow-hidden border border-white/10 bg-white/5" data-testid={`store-screenshot-${i}`}>
                <img src={mediaUrl(s.image_url)} alt={s.caption || `Screenshot ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
                {s.caption && <p className="p-2 text-xs font-semibold text-white/80 truncate">{s.caption}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {k.long_desc && (
          <div className="mb-8">
            <h2 className="text-sm font-bold text-white/50 uppercase tracking-wide mb-2">About</h2>
            <p className="text-sm sm:text-base leading-relaxed text-white/85 whitespace-pre-wrap" data-testid="store-desc">{k.long_desc}</p>
          </div>
        )}

        {/* Features */}
        {(k.features || []).length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-bold text-white/50 uppercase tracking-wide mb-2">Features</h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {k.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/85"><span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: accent }} /> {f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Hashtags */}
        {(k.hashtags || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="store-hashtags">
            {k.hashtags.map((h, i) => <span key={i} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/70">#{h}</span>)}
          </div>
        )}

        {/* Scan to play + share */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-10" data-testid="store-qr">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2 rounded-xl shrink-0">
              <QRCodeSVG value={qrUrl} size={92} bgColor="#ffffff" fgColor="#0b0b0f" level="M" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold">Scan to play on your phone</p>
              <p className="text-xs text-white/50 mt-0.5">Point your camera here to open {k.game_title} instantly.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="store-share">
            <button onClick={() => openShare("x")} data-testid="store-share-x" className="rounded-full px-3.5 py-1.5 text-xs font-bold bg-black text-white border border-white/25 hover:opacity-90 transition-opacity">𝕏  Share</button>
            <button onClick={() => openShare("whatsapp")} data-testid="store-share-whatsapp" className="rounded-full px-3.5 py-1.5 text-xs font-bold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: "#25D366" }}>WhatsApp</button>
            <button onClick={() => openShare("discord")} data-testid="store-share-discord" className="rounded-full px-3.5 py-1.5 text-xs font-bold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: "#5865F2" }}>Discord</button>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">Made with GameView Studio</p>
          <Link to="/"><Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10">Make your own</Button></Link>
        </div>
      </div>
    </div>
  );
}
